import { pack } from '@attain-apps/data-utils'
import * as errors from '@attain-apps/errors'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import { Pagination } from './pagination'

/** Describes one GET request made by an import definition. */
export interface ApiSource {
  /**
   * API path appended to the configured connector's base URL.
   *
   * @example '/rest/api/3/search'
   * @example '/rest/api/3/issue/ATTAIN-123'
   * @example '/rest/api/3/project/search'
   * @example '/rest/api/3/users/search'
   */
  path: string
  /**
   * Fixed query parameters sent with every request.
   *
   * @example { jql: 'project = ATTAIN' }
   * @example { jql: 'project = ATTAIN AND status = "In Progress"', maxResults: '100' }
   * @example { projectId: '10001', expand: 'description,lead' }
   */
  query?: Readonly<Record<string, string>>
  /**
   * Whether the value selected by {@link recordsPath} is one record or a list
   * of records. A Jira issue search selects an array of issues.
   */
  responseType: 'object' | 'array'
  /**
   * Dot-separated path to the records within a wrapped API response. Omit it
   * when the response itself is the record or array.
   *
   * @example 'issues'
   * @example 'values' for a Jira response shaped as `{ values: [...] }`
   * @example 'data.issues' for a response shaped as `{ data: { issues: [...] } }`
   * @example undefined for a response shaped directly as `[...]` or `{ ... }`
   */
  recordsPath?: string
  /**
   * How this endpoint offers the records that do not fit in one response. Omit
   * it for endpoints that return everything they have at once, which are then
   * requested exactly once.
   *
   * @see {@link Pagination}
   */
  pagination?: Pagination
}
export namespace ApiSource {
  /** Extracts records from a response according to the API source contract. */
  export function extract(
    source: Readonly<ApiSource>,
    response: unknown,
  ): readonly Readonly<Record<string, unknown>>[] {
    let value = response
    for (const property of source.recordsPath?.split('.') ?? []) {
      if (!TypeChecker.isObject(value) || Array.isArray(value)) {
        throw invalidResponse(source.recordsPath)
      }
      // NOTE: The runtime check above proves this is a non-array object. We
      // cast it to a record so it can be indexed by the configured property.
      value = (value as Readonly<Record<string, unknown>>)[property]
    }

    if (source.responseType === 'array') {
      if (
        !Array.isArray(value) ||
        !value.every(
          record => TypeChecker.isObject(record) && !Array.isArray(record),
        )
      ) {
        throw invalidResponse(source.recordsPath)
      }
      // NOTE: The every() check above proves every array item is a non-array
      // object, but TypeScript does not preserve that refinement for the array.
      return value as readonly Readonly<Record<string, unknown>>[]
    }

    if (!TypeChecker.isObject(value) || Array.isArray(value)) {
      throw invalidResponse(source.recordsPath)
    }
    // NOTE: The runtime check above proves this is one non-array object. We
    // cast it to the record type returned by this extraction operation.
    return pack(value as Readonly<Record<string, unknown>>)
  }

  export const isApiSource: TypeChecker<ApiSource> =
    TypeChecker.Factory.object<ApiSource>({
      pagination: TypeChecker.Factory.optional(Pagination.isPagination),
      query: TypeChecker.Factory.optional(
        TypeChecker.Factory.record(TypeChecker.isString, TypeChecker.isString),
      ),
      recordsPath: TypeChecker.Factory.optional(TypeChecker.isString),
      responseType: TypeChecker.Factory.union(
        TypeChecker.Factory.exact('object'),
        TypeChecker.Factory.exact('array'),
      ),
      path: TypeChecker.isString,
    }).usingAlias('integrations.ApiSource')

  export const assertIsApiSource: TypeAsserter<ApiSource> =
    TypeAsserter.Factory.from(isApiSource)

  /** Creates an error for a response that does not match its API source. */
  function invalidResponse(
    recordsPath: string | undefined,
  ): errors.ValidationError {
    return new errors.ValidationError({
      reason: recordsPath
        ? `Expected records at '${recordsPath}' to match the configured response type.`
        : 'Expected the response to match the configured response type.',
      type: 'integration API response',
    })
  }
}
